import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { FileTypes } from 'utils/constants';
import { collectVideoEvidence, isValidVideoEvidenceRange, isVideoFile } from 'utils/videoGrading';

const EvidenceReviewContext = createContext(null);

export const useEvidenceReview = () => useContext(EvidenceReviewContext);

export const EvidenceReviewProvider = ({
    children,
    assignment,
    submission,
    criteria = [],
    selectedFileId,
    setSelectedFileId,
    selectedCriterionId,
    onSelectCriterion,
}) => {
    const [navigationRequest, setNavigationRequest] = useState(null);
    const sequence = useRef(0);
    const file = submission?.files?.find(({ id }) => String(id) === String(selectedFileId));
    const rubricCriteria = assignment?.rubric?.criteria || criteria;
    const videoEvidence = useMemo(
        () =>
            collectVideoEvidence({
                grading: submission?.ai?.grading,
                criteria: rubricCriteria,
                file,
                allowUnscoped: submission?.files?.length === 1,
            }),
        [file, rubricCriteria, submission?.ai?.grading, submission?.files?.length]
    );

    const navigateToEvidence = useCallback(
        (target, { requestedTime } = {}) => {
            if (!target?.fileId) return;
            const file = submission?.files?.find(({ id }) => String(id) === String(target.fileId));
            if (!file) return;

            setSelectedFileId(file.id);
            if (target.criterionId && rubricCriteria.some(({ id }) => id === target.criterionId)) {
                onSelectCriterion?.(target.criterionId);
            }

            sequence.current += 1;
            const start = Number.isFinite(requestedTime)
                ? Math.max(target.start, Math.min(requestedTime, target.end))
                : target.start;
            setNavigationRequest({
                id: sequence.current,
                submissionId: submission.id,
                fileId: file.id,
                target: {
                    ...target,
                    fileId: file.id,
                    ...(target.category === FileTypes.VIDEO && { start, end: target.end }),
                },
            });
        },
        [onSelectCriterion, rubricCriteria, setSelectedFileId, submission]
    );

    const seekToEvidence = useCallback(
        (target, requestedTime = target?.start) => {
            const file = submission?.files?.find(({ id }) => String(id) === String(target?.fileId));
            if (!isVideoFile(file) || !isValidVideoEvidenceRange(target)) return;
            navigateToEvidence(target, { requestedTime });
        },
        [navigateToEvidence, submission]
    );

    const completeNavigationRequest = useCallback((requestId) => {
        setNavigationRequest((current) => (current?.id === requestId ? null : current));
    }, []);

    const requestForCurrentFile =
        navigationRequest?.submissionId === submission?.id &&
        String(navigationRequest?.fileId) === String(selectedFileId)
            ? navigationRequest
            : null;

    useEffect(() => {
        if (navigationRequest && navigationRequest.submissionId !== submission?.id) {
            setNavigationRequest(null);
        }
    }, [navigationRequest, submission?.id]);

    const value = useMemo(
        () => ({
            file,
            criteria: rubricCriteria,
            videoEvidence,
            navigationRequest: requestForCurrentFile,
            navigateToEvidence,
            seekToEvidence,
            completeNavigationRequest,
            selectedCriterionId,
        }),
        [
            completeNavigationRequest,
            file,
            navigateToEvidence,
            requestForCurrentFile,
            rubricCriteria,
            seekToEvidence,
            selectedCriterionId,
            videoEvidence,
        ]
    );

    return <EvidenceReviewContext.Provider value={value}>{children}</EvidenceReviewContext.Provider>;
};
